import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { Button as ButtonShad } from 'src/components/ui/button';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { array, number, object } from 'yup';
import { EditIcon } from 'src/assets/icons/editIcon';
import {
  baseApi,
  useAssignProjectKnowledgeBaseMutation,
  useUnassignProjectKnowledgeBaseMutation,
} from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useGetKnowledgeBaseQuery } from 'src/services/knowledgeBaseService';
import { SearchFilesContainer } from 'src/components/SearchFilesContainer/SearchFilesContainer';
import { useDebounce } from 'src/hooks/useDebounce';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { highlightMatchHelper } from 'src/helpers/highlightMatchHelper';
import { Checkbox } from 'src/components/ui/checkbox';
import { ProductsTypeBack } from 'src/constants/products';
import { useAppDispatch } from 'src/store';
import type { Project } from 'src/services/projectsService';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';

// Checked knowledge bases, plus the ones to assign and unassign compared with the current ones.
interface ChangeKnowledgeBaseFormValues {
  knowledgeBaseId: number[];
  uncheckedKnowledgeBaseId: number[];
  knowledgeBaseIdNew: number[];
}

export const ChangeKnowledgeBaseModal = ({
  disabled,
  currentProject,
  selectedKBIds = [],
}: {
  disabled: boolean;
  currentProject?: Project;
  selectedKBIds?: number[];
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [filteredValue, setFilteredValue] = useState('');

  const searchNameDebounce = useDebounce(filteredValue, 500);

  const {
    data: knowledgeBaseData,
    isError: isErrorKnowledgeBaseData,
    isLoading: isLoadingKnowledgeBaseData,
  } = useGetKnowledgeBaseQuery(
    {
      load_projects: true,
      name_filter: searchNameDebounce,
      page: page,
      page_size: 20,
    },
    { refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (knowledgeBaseData) {
      const items = knowledgeBaseData.items || [];

      setKnowledgeBases(page === 1 ? [...items] : [...knowledgeBases, ...items]);
    }
  }, [knowledgeBaseData]);

  useEffect(() => {
    setPage(1);
  }, [searchNameDebounce]);

  const fetchOnScroll = () => {
    setPage(page + 1);
  };

  const onHandleSearch = (value: string) => {
    setFilteredValue(value);
  };
  const [assignKB, { isLoading: assignKBisLoading, isSuccess: assignKBisSuccess }] =
    useAssignProjectKnowledgeBaseMutation();
  const [unassignKB, { isLoading: unassignKBIsLoading, isSuccess: unassignKBisSuccess }] =
    useUnassignProjectKnowledgeBaseMutation();

  useEffect(() => {
    if (assignKBisSuccess) {
      notification.success('Knowledge Base was changed!');
      setIsDialogOpen(false);
    }
  }, [assignKBisSuccess, assignKBisLoading]);

  useEffect(() => {
    if (unassignKBisSuccess) {
      notification.success('Knowledge Base was changed!');
      setIsDialogOpen(false);
    }
  }, [unassignKBisSuccess, unassignKBIsLoading]);

  const {
    setValue,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isDirty },
  } = useForm<ChangeKnowledgeBaseFormValues>({
    defaultValues: {
      knowledgeBaseId: selectedKBIds,
      uncheckedKnowledgeBaseId: [],
      knowledgeBaseIdNew: [],
    },
    resolver: formResolver(
      object({
        knowledgeBaseId: array(number()).required().label('Knowledge Base'),
      }),
    ),
  });

  const dialogOpenHandler = (open: boolean) => {
    setIsDialogOpen(open);
  };

  const submit = (data: ChangeKnowledgeBaseFormValues) => {
    if (data.knowledgeBaseIdNew.length) {
      assignKB({
        project_type: ProductsTypeBack[currentProject?.project_type],
        projectId: currentProject.id,
        body: data.knowledgeBaseIdNew,
      }).then(() => dispatch(baseApi.util.invalidateTags(['KnowledgeBase'])));
    }
    if (data.uncheckedKnowledgeBaseId.length) {
      unassignKB({
        project_type: ProductsTypeBack[currentProject?.project_type],
        projectId: currentProject.id,
        body: data.uncheckedKnowledgeBaseId,
      }).then(() => dispatch(baseApi.util.invalidateTags(['KnowledgeBase'])));
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading: isLoadingKnowledgeBaseData,
    hasNextPage: knowledgeBaseData?.page_info?.has_next_page,
    onLoadMore: fetchOnScroll,
    disabled: !!isErrorKnowledgeBaseData,
    rootMargin: '0px 0px 500px 0px',
  });

  const checkBoxHandler = (
    checked: boolean | 'indeterminate',
    onChange: (ids: number[]) => void,
    value: number[],
    product: Pick<KnowledgeBase, 'id'>,
  ) => {
    const id = Number(product.id);
    const currentUnchecked = getValues('uncheckedKnowledgeBaseId');
    const knowledgeBaseIdNewForRes = getValues('knowledgeBaseIdNew');

    if (checked) {
      onChange([...value, id]);

      if (!selectedKBIds.includes(id)) {
        setValue('knowledgeBaseIdNew', [...knowledgeBaseIdNewForRes, id]);
      }

      if (selectedKBIds.includes(id)) {
        setValue(
          'uncheckedKnowledgeBaseId',
          currentUnchecked.filter((item) => item !== id),
        );
      }
    } else {
      onChange(value.filter((val) => val !== id));
      setValue(
        'knowledgeBaseIdNew',
        knowledgeBaseIdNewForRes.filter((val) => val !== id),
      );

      if (selectedKBIds.includes(id) && !currentUnchecked.includes(id)) {
        setValue('uncheckedKnowledgeBaseId', [...currentUnchecked, id]);
      }
    }
  };

  return (
    <Dialog onOpenChange={dialogOpenHandler} open={isDialogOpen}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <ButtonShad
          variant="ghost"
          size="icon"
          className={clsx(disabled && 'pointer-events-none opacity-50')}
        >
          <EditIcon />
        </ButtonShad>
      </DialogTrigger>
      <DialogContent className="pb-0">
        <DialogHeader>
          <DialogTitle>Change knowledge base</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="w-full h-full flex justify-between min-h-9">
            <p className="opacity-50 font-medium text-xs flex items-center">
              All available knowledge base
            </p>
            <SearchFilesContainer onSearch={onHandleSearch} hideSort hideViewType />
          </div>
          <form
            role="none"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(submit)();
              }
            }}
            onSubmit={handleSubmit(submit)}
            id="model-form"
            className="bg-white rounded-sm-5 flex flex-col space-y-1"
          >
            <Controller
              control={control}
              name="knowledgeBaseId"
              render={({ field: { onChange, value } }) => (
                <>
                  {knowledgeBases.map((base) => {
                    return (
                      <label
                        ref={sentryRef}
                        htmlFor={String(base.id)}
                        key={base.id}
                        className="flex items-center space-x-5 space-y-0 border rounded-sm-5 px-5 py-4 cursor-pointer hover:border-black transition-colors"
                      >
                        <Checkbox
                          id={String(base.id)}
                          checked={value?.includes(base.id)}
                          onCheckedChange={(checked) => {
                            checkBoxHandler(checked, onChange, value, base);
                          }}
                        />
                        <label htmlFor={String(base.id)} className="cursor-pointer">
                          <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                            {highlightMatchHelper(base.name, filteredValue)}
                          </p>
                        </label>
                      </label>
                    );
                  })}
                </>
              )}
            />
            {errors?.knowledgeBaseId?.message && (
              <p className="text-sm font-semibold text-error absolute top-full mt-3">
                {errors.knowledgeBaseId.message}
              </p>
            )}
          </form>
        </div>
        <DialogFooter className="sticky bottom-0 bg-white pb-6">
          <DialogClose asChild>
            <ButtonShad variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </ButtonShad>
          </DialogClose>
          <ButtonShad
            variant="pill-blue"
            size="pill"
            disabled={assignKBisLoading || unassignKBIsLoading || !isDirty}
            type="submit"
            form="model-form"
            style={{ width: 'auto' }}
          >
            {assignKBisLoading || unassignKBIsLoading ? 'Loading...' : 'Change'}
          </ButtonShad>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
